import React, { useState, useMemo } from 'react'
import { ClientScreenshot } from '../../hooks/useClientScreenshots'

interface ScreenshotGalleryProps {
  screenshots: ClientScreenshot[]
  selectedScreenshotId: string | null
  onScreenshotSelect: (id: string) => void
  onScreenshotDelete: (id: string) => void
  onCopyScreenshot: (id: string) => Promise<boolean>
  onCopySelected: (selectedIds: string[]) => Promise<boolean>
  onClearAll: () => void
  maxScreenshots: number
  memoryUsage: { totalMB: number; avgMBPerScreenshot: number }
}

export const ScreenshotGallery: React.FC<ScreenshotGalleryProps> = ({
  screenshots,
  selectedScreenshotId,
  onScreenshotSelect,
  onScreenshotDelete,
  onCopyScreenshot,
  onCopySelected,
  onClearAll,
  maxScreenshots,
  memoryUsage
}) => {
  const [multiSelectMode, setMultiSelectMode] = useState(false)
  const [selectedForCopy, setSelectedForCopy] = useState<Set<string>>(new Set())
  const [copyingId, setCopyingId] = useState<string | null>(null)

  // Format time for display
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  // Memory usage color coding
  const memoryUsageColor = useMemo(() => {
    if (memoryUsage.totalMB < 50) return 'text-green-600'
    if (memoryUsage.totalMB < 100) return 'text-yellow-600'
    return 'text-red-600'
  }, [memoryUsage.totalMB])

  const handleMultiSelectToggle = (id: string) => {
    setSelectedForCopy(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleCopySelected = async () => {
    if (selectedForCopy.size === 0) return
    
    setCopyingId('multiple')
    try {
      const success = await onCopySelected(Array.from(selectedForCopy))
      if (success) {
        setSelectedForCopy(new Set())
        setMultiSelectMode(false)
      }
    } finally {
      setCopyingId(null)
    }
  }

  const handleCopySingle = async (id: string) => {
    setCopyingId(id)
    try {
      await onCopyScreenshot(id)
    } finally {
      setCopyingId(null)
    }
  }

  const handleDeleteConfirm = (id: string) => {
    const screenshot = screenshots.find(s => s.id === id)
    if (!screenshot) return
    
    const time = formatTime(screenshot.createdAt)
    if (confirm(`Delete screenshot from ${time}?`)) {
      onScreenshotDelete(id)
    }
  }

  if (screenshots.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <div className="text-gray-400 text-lg mb-2">📷</div>
        <p className="text-gray-600 text-sm">No screenshots yet</p>
        <p className="text-gray-500 text-xs mt-1">Take a screenshot to get started</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Screenshots ({screenshots.length}/{maxScreenshots})
            </h3>
            <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
              <span className={memoryUsageColor}>
                Memory: {memoryUsage.totalMB.toFixed(1)}MB
              </span>
              <span>
                Avg: {memoryUsage.avgMBPerScreenshot.toFixed(1)}MB/shot
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {screenshots.length > 1 && (
              <button
                onClick={() => {
                  setMultiSelectMode(!multiSelectMode)
                  setSelectedForCopy(new Set())
                }}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  multiSelectMode 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {multiSelectMode ? 'Cancel' : 'Multi-select'}
              </button>
            )}
            
            {screenshots.length > 0 && (
              <button
                onClick={onClearAll}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
        
        {/* Multi-select actions */}
        {multiSelectMode && (
          <div className="mt-3 p-3 bg-blue-50 rounded-md">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-700">
                {selectedForCopy.size} selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSelectedForCopy(new Set(screenshots.map(s => s.id)))}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Select All
                </button>
                
                {selectedForCopy.size > 0 && (
                  <button
                    onClick={handleCopySelected}
                    disabled={copyingId === 'multiple'}
                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                  >
                    {copyingId === 'multiple' ? 'Copying...' : `Copy ${selectedForCopy.size}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Screenshot Grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {screenshots.map((screenshot) => {
            const isSelected = selectedScreenshotId === screenshot.id
            const isSelectedForCopy = selectedForCopy.has(screenshot.id)
            const isCopying = copyingId === screenshot.id
            
            return (
              <div
                key={screenshot.id}
                className={`relative rounded-lg border-2 transition-all cursor-pointer ${
                  isSelected 
                    ? 'border-blue-500 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                } ${
                  isSelectedForCopy ? 'ring-2 ring-blue-400' : ''
                }`}
                onClick={() => {
                  if (multiSelectMode) {
                    handleMultiSelectToggle(screenshot.id)
                  } else {
                    onScreenshotSelect(screenshot.id)
                  }
                }}
              >
                {/* Multi-select checkbox */}
                {multiSelectMode && (
                  <div className="absolute top-2 left-2 z-10">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelectedForCopy 
                        ? 'bg-blue-500 border-blue-500' 
                        : 'bg-white border-gray-400'
                    }`}>
                      {isSelectedForCopy && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                )}

                {/* Screenshot thumbnail */}
                <img
                  src={screenshot.thumbnail || screenshot.dataUrl}
                  alt={`Screenshot ${formatTime(screenshot.createdAt)}`}
                  className="w-full h-24 object-cover rounded-t-lg bg-gray-100"
                  loading="lazy"
                />
                
                {/* Screenshot info */}
                <div className="p-2">
                  <div className="text-xs text-gray-600 mb-1">
                    {formatTime(screenshot.createdAt)}
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {screenshot.annotations.length} annotation{screenshot.annotations.length !== 1 ? 's' : ''}
                    </span>
                    
                    {isSelected && (
                      <span className="bg-blue-100 text-blue-600 px-1 py-0.5 rounded text-xs">
                        Active
                      </span>
                    )}
                  </div>
                </div>

                {/* Action buttons (only show on hover when not in multi-select mode) */}
                {!multiSelectMode && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 transition-all rounded-lg opacity-0 hover:opacity-100">
                    <div className="absolute inset-0 flex items-center justify-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCopySingle(screenshot.id)
                        }}
                        disabled={isCopying}
                        className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                      >
                        {isCopying ? '...' : 'Copy'}
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteConfirm(screenshot.id)
                        }}
                        className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Memory warning */}
      {memoryUsage.totalMB > 100 && (
        <div className="p-4 border-t border-gray-200 bg-yellow-50">
          <div className="flex items-start space-x-2">
            <div className="text-yellow-600">⚠️</div>
            <div>
              <p className="text-sm text-yellow-800 font-medium">High Memory Usage</p>
              <p className="text-xs text-yellow-700 mt-1">
                Consider deleting some screenshots to improve performance on lower-spec devices.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}